import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff, Video, VideoOff, RefreshCw } from "lucide-react";
import { Button, cn, Alert, AlertDescription } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useLocalTracks } from "../hooks/useLocalTracks";
import { useMeetingOperations } from "../hooks/useMeetingOperations";
import { useAudioLevel } from "../hooks/useAudioLevel";
import { LocalPreview } from "./LocalPreview";
import { BackgroundEffectSelector } from "./BackgroundEffectSelector";
import type { WaitingRoomViewProps } from "../types";

/**
 * WaitingRoomView - Pre-meeting lobby with local preview.
 *
 * Shows local camera preview, audio/video toggles, and start/join button.
 */
export function WaitingRoomView({ localUser }: WaitingRoomViewProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { createLocalTracks, toggleVideo, toggleAudio } = useLocalTracks();
  const { startMeeting, joinMeeting, leaveMeeting, isStarting, isJoining } = useMeetingOperations();

  const { isVideoEnabled, isAudioEnabled, meetingMeta, localVideoTrack, localAudioTrack, deviceError } = videoCalling;

  const { calendarEventId, isHost, eventTitle } = meetingMeta;

  // Monitor audio level for visual feedback on mic button
  const audioLevel = useAudioLevel(localAudioTrack, isAudioEnabled);

  // Retry creating tracks
  const handleRetry = useCallback(() => {
    videoCalling.setDeviceError(null);
    createLocalTracks().catch(() => {
      // Error is handled and stored in state by createLocalTracks
    });
  }, [videoCalling, createLocalTracks]);

  const handleStartOrJoin = async () => {
    if (!calendarEventId) return;

    try {
      if (isHost) {
        await startMeeting(calendarEventId);
      } else {
        await joinMeeting(calendarEventId);
      }
    } catch (error) {
      console.error("Failed to start/join meeting:", error);
    }
  };

  const handleLeave = () => {
    leaveMeeting();
  };

  const isLoading = isStarting || isJoining;

  // Determine if we have working tracks
  const hasWorkingTracks = localVideoTrack || localAudioTrack;

  return (
    <div className="flex flex-col p-4 gap-4">
      {/* Event title */}
      {eventTitle && <h3 className="text-lg font-semibold text-center text-foreground">{eventTitle}</h3>}

      {/* Device error alert */}
      {deviceError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span className="text-sm">{deviceError}</span>
            <Button variant="ghost" size="sm" onClick={handleRetry} className="shrink-0">
              <RefreshCw className="h-4 w-4 mr-1" />
              {t("Retry")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Local camera preview */}
      <LocalPreview localUser={localUser} className="max-h-[240px]" />

      {/* Media controls */}
      <div className="flex justify-center gap-3">
        {/* Mic button with audio level glow */}
        <div className="relative">
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleAudio}
                disabled={!hasWorkingTracks}
                className={cn(
                  "relative rounded-full h-10 w-10 transition-all duration-100",
                  !isAudioEnabled && "bg-red-500/20 text-red-400 border-red-400/50"
                )}
                style={
                  isAudioEnabled && audioLevel > 0.04
                    ? {
                        borderColor: `rgb(34, 197, 94)`, // green-500
                        backgroundColor: `rgba(34, 197, 94, ${Math.min((audioLevel - 0.04) * 2, 0.3)})`,
                        boxShadow: `0 0 ${Math.round((audioLevel - 0.04) * 40)}px ${Math.round(
                          (audioLevel - 0.04) * 20
                        )}px rgba(34, 197, 94, ${Math.min((audioLevel - 0.04) * 3, 0.6)})`,
                      }
                    : undefined
                }
              >
                {isAudioEnabled ? (
                  <Mic
                    className="h-4 w-4 transition-colors duration-100"
                    style={audioLevel > 0.04 ? { color: "rgb(34, 197, 94)" } : undefined}
                  />
                ) : (
                  <MicOff className="h-4 w-4" />
                )}
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content>{isAudioEnabled ? t("Mute") : t("Unmute")}</Tooltip.Content>
          </Tooltip>
        </div>

        <Tooltip>
          <Tooltip.Trigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={toggleVideo}
              disabled={!hasWorkingTracks}
              className={cn(
                "rounded-full h-10 w-10",
                !isVideoEnabled && "bg-red-500/20 text-red-400 border-red-400/50"
              )}
            >
              {isVideoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>{isVideoEnabled ? t("Turn off camera") : t("Turn on camera")}</Tooltip.Content>
        </Tooltip>

        <BackgroundEffectSelector variant="lobby" disabled={!hasWorkingTracks} />
      </div>

      {/* Status message */}
      {!isHost && !deviceError && (
        <p className="text-sm text-center text-muted-foreground">{t("Waiting for host to start...")}</p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 justify-center">
        <Button
          onClick={handleStartOrJoin}
          disabled={isLoading || !calendarEventId || !!deviceError}
          className="flex-1 max-w-[200px]"
        >
          {isLoading ? (isHost ? t("Starting...") : t("Joining...")) : isHost ? t("Start Meeting") : t("Join Meeting")}
        </Button>

        <Button variant="outline" onClick={handleLeave}>
          {t("Leave")}
        </Button>
      </div>
    </div>
  );
}
